import React, { useCallback, useEffect, useRef, useState } from "react";

import { initializeApp } from 'firebase/app';
import { GoogleAuthProvider, getAuth, signInWithPopup } from "firebase/auth";
import { Alert, AlertColor, Box, Button, Container, Snackbar, styled, Typography, useMediaQuery} from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useMutation, useQuery } from 'react-query';

import ErrorBoundary from "./components/ErrorBoundary";

import { LoadingButton } from "@mui/lab";
import { fetchJWTToken } from "./utils/queries/users";

const firebaseConfig = {
  apiKey: "AIzaSyDDCa_mB89KWpNDjB2Ef6htZIuyCylYbkI",
  authDomain: "venturify.xyz",
  projectId: "venturify-963c5",
  storageBucket: "venturify-963c5.appspot.com",
  messagingSenderId: "1042437780187",
  appId: "1:1042437780187:web:eb4936cc8b5486721f92fe",
  measurementId: "G-SFX6DZQXMZ"
};

initializeApp(firebaseConfig);

const provider = new GoogleAuthProvider();

provider.addScope('https://www.googleapis.com/auth/userinfo.email');

const auth = getAuth();

const AppContainer = styled(Box)({
  height: "100vh",
  overflow: "auto",
  width: "100vw",
  "@media print": {
    height: "auto",
  }
})

const StyledSnackbar = styled(Snackbar)({
  "@media print": {
    display: "none",
  }
});

interface AppProps {
  Component: any;
  containerSx?: any;
  scenic?: boolean;
};

const App = ({ containerSx, Component, scenic }: AppProps) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: scenic
          ? {
              mode: 'light',
              primary: { main: '#1c1916' },
              text: { primary: '#1c1916', secondary: '#5c574e' },
              background: { default: 'transparent', paper: '#ffffff' },
            }
          : {
              mode: prefersDarkMode ? 'dark' : 'light',
            },
        typography: {
          fontFamily: scenic
            ? '"Montserrat", sans-serif'
            : '"Plus Jakarta Sans", "Montserrat", sans-serif',
        },
      }),
    [prefersDarkMode, scenic],
  );

  const [toast, setToast] = useState<{ message: string, severity: string } | undefined>()
  const [userToken, setUserToken] = useState(localStorage.getItem('jwtToken') || undefined);
  const [userData, setUserData] = useState();
  const [isFetchingJWT, setIsFetchingJWT] = useState(false);
  const exchangingToken = useRef(false);
  const exchangeGeneration = useRef(0);
  const sessionRefresh = useRef(false);

  const exchangeFirebaseSession = useCallback(async (firebaseUser: { uid: string; email: string | null }, force = false) => {
    const storedUserToken = localStorage.getItem('jwtToken');
    if (storedUserToken && !force) {
      setUserToken(storedUserToken);
      return;
    }

    if (exchangingToken.current) return;
    exchangingToken.current = true;
    const generation = exchangeGeneration.current;
    setIsFetchingJWT(true);

    try {
      const token = await fetchJWTToken({
        userId: firebaseUser.uid,
        email: firebaseUser.email,
      });
      if (generation !== exchangeGeneration.current) return;
      localStorage.setItem('jwtToken', token);
      setUserToken(token);
    } catch {
      if (generation !== exchangeGeneration.current) return;
      setToast({
        message: "Could not finish signing in. Please try again.",
        severity: "error",
      });
    } finally {
      if (generation === exchangeGeneration.current) {
        exchangingToken.current = false;
        setIsFetchingJWT(false);
      }
    }
  }, []);

  const handleGoogleSignIn = () => signInWithPopup(auth, provider).then((result) => {
    if (result.user) return exchangeFirebaseSession(result.user);
  });

  const handleGoogleSignOut = () => auth.signOut()
  .catch(() => {
    setToast({
      message: "Error signing out",
      severity: "error"
    })
  });

  const { mutate: handleSignIn, isLoading: isSigningIn } = useMutation(handleGoogleSignIn);
  const { mutate: handleSignOut, isLoading: isSigningOut } = useMutation(handleGoogleSignOut);

  const { isLoading: isLoadingUserData } = useQuery({
    enabled: !userData && Boolean(userToken) && !isFetchingJWT,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    onSuccess: setUserData,
    queryKey: ['users'],
  });

  useEffect(() => {
    if (userData) sessionRefresh.current = false;
  }, [userData]);

  useEffect(() => {
    if (!toast) {
      if (isLoadingUserData || isFetchingJWT){
        setToast({
          message: "Signing in",
          severity: "success",
        }) 
      }
    } else if (toast?.message === "Signing in" && !isFetchingJWT && !isLoadingUserData) setToast(undefined)
  }, [toast, isFetchingJWT, isLoadingUserData])

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (!firebaseUser) {
        exchangeGeneration.current += 1;
        exchangingToken.current = false;
        sessionRefresh.current = false;
        setIsFetchingJWT(false);
        localStorage.removeItem('jwtToken');
        setUserToken(undefined);
        setUserData(undefined);
        return;
      }

      exchangeFirebaseSession(firebaseUser);
    });

    const onSessionRejected = () => {
      setUserToken(undefined);
      setUserData(undefined);
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || sessionRefresh.current || exchangingToken.current) return;
      sessionRefresh.current = true;
      exchangeFirebaseSession(firebaseUser, true);
    };
    window.addEventListener("venturify-session-rejected", onSessionRejected);

    return () => {
      unsubscribe();
      window.removeEventListener("venturify-session-rejected", onSessionRejected);
    };
  }, [exchangeFirebaseSession]);

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <AppContainer
          className={scenic ? "landing" : undefined}
          sx={scenic ? {
            bgcolor: '#f3efe8',
          } : {
            bgcolor: (theme) => theme.palette.background.paper,
          }}
        >
          <Container
            maxWidth={scenic ? false : undefined}
            sx={{
              height: "100vh",
              margin: scenic ? 0 : { xs: 0, sm: "0 auto" },
              maxWidth: scenic ? "100%" : { xs: "100%", sm: "lg" },
              px: scenic ? { xs: 2.5, sm: 6, md: 8 } : undefined,
              ...containerSx,
            }}
          >
            <StyledSnackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(undefined)}>
              <Alert severity={(toast?.severity || "info") as AlertColor} sx={{ width: '100%' }}>
                {toast?.message}
              </Alert>
            </StyledSnackbar>
            <Box height={scenic ? "84px" : "80px"} display="flex" alignItems="center" justifyContent="space-between">
              <Button href="/" sx={{ minWidth: 0, textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent' } }}>
                <Typography sx={{
                  fontFamily: scenic ? '"Montserrat", sans-serif' : '"Plus Jakarta Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: 22, sm: 26 },
                  letterSpacing: '-0.04em',
                  lineHeight: 1,
                  color: scenic || !prefersDarkMode ? '#1c1916' : '#EDE9FE',
                  animation: "bgColor 10s infinite linear",
                  borderRadius: "4px",
                  px: 0.75,
                  py: 0.5,
                }}>
                  Venturify
                  <Box component="span" sx={{ color: scenic || !prefersDarkMode ? '#7a756c' : '#c8c4bc', fontWeight: 600 }}>.xyz</Box>
                </Typography>
              </Button>
              {Boolean(userData) ? (
                <LoadingButton
                  variant="text"
                  loading={isSigningOut}
                  onClick={() => handleSignOut()}
                  sx={{
                    minWidth: "72px",
                    color: scenic || !prefersDarkMode ? '#1c1916' : '#EDE9FE',
                    fontWeight: 600,
                    fontSize: 16,
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'transparent', color: scenic || !prefersDarkMode ? '#000' : '#fff' },
                  }}
                >
                  Log out
                </LoadingButton>
              ) : (
                <LoadingButton
                  variant="text"
                  loading={isSigningIn}
                  onClick={() => handleSignIn()}
                  sx={{
                    minWidth: "72px",
                    color: scenic || !prefersDarkMode ? '#1c1916' : '#EDE9FE',
                    fontWeight: 600,
                    fontSize: 16,
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'transparent', color: scenic || !prefersDarkMode ? '#000' : '#fff' },
                  }}
                >
                  Log in
                </LoadingButton>
              )}
            </Box>
            <Box width="100%" height={scenic ? "calc(100% - 84px)" : "calc(100% - 80px)"}>
              <Component userData={userData} isLoadingUserData={isLoadingUserData} setToast={setToast} isFetchingJWT={isFetchingJWT} userToken={userToken} handleSignIn={handleSignIn} handleSignOut={handleSignOut} isSigningIn={isSigningIn} isSigningOut={isSigningOut} />
            </Box>
          </Container>
        </AppContainer>
      </ErrorBoundary>
    </ThemeProvider>
  )};

export default App;
